import { EntityAdapter, EntityState, createEntityAdapter } from '@ngrx/entity';
import { Action, createReducer, on } from '@ngrx/store';
import { CalculatedRole } from '../../../models';
import { RoleActions } from '../../actions';
import { Role } from '@anecdotes/api-contracts';

export interface RolesState {
  loaded: boolean;
  loading: boolean;
  roles: EntityState<CalculatedRole>;
  rolesBeingUpdated: { [roleKey: string]: Role };
  rolesBeingDeleted: { [roleKey: string]: Role };
}

function selectEventId(role: CalculatedRole): string {
  return role.key;
}

const roleAdapter: EntityAdapter<CalculatedRole> = createEntityAdapter<any>({
  selectId: selectEventId,
});

const initialState: RolesState = {
  loading: false,
  loaded: false,
  roles: roleAdapter.getInitialState(),
  rolesBeingDeleted: {},
  rolesBeingUpdated: {},
};

const adapterReducer = createReducer(
  initialState,
  on(RoleActions.loadRoles, (state) => {
    return {
      ...state,
      loaded: false,
      loading: true
    };
  }),
  on(RoleActions.rolesLoaded, (state, action) => {
    return {
      ...state,
      loading: false,
      loaded: true,
      roles: roleAdapter.setAll(action.roles, {
        ...state.roles,
      }),
    };
  }),
  on(RoleActions.roleCreated, (state, action) => {
    return {
      ...state,
      roles: roleAdapter.addOne(action.role, state.roles)
    };
  }),
  on(RoleActions.updateRole, (state, action) => {
    return {
      ...state,
      rolesBeingUpdated: {
        ...state.rolesBeingUpdated,
        [action.role.key]: state.roles.entities[action.role.key]
      },
      roles: roleAdapter.updateOne(
        {
          id: action.role.key,
          changes: action.role,
        },
        state.roles,
      )
    };
  }),
  on(RoleActions.roleUpdated, (state, action) => {
    const rolesBeingUpdated = {...state.rolesBeingUpdated};
    delete rolesBeingUpdated[action.role.key];

    return {
      ...state,
      rolesBeingUpdated,
      roles: roleAdapter.updateOne(
        {
          id: action.role.key,
          changes: action.role,
        },
        state.roles,
      )
    };
  }),
  on(RoleActions.roleUpdateFailed, (state, action) => {
    const rolesBeingUpdated = {...state.rolesBeingUpdated};
    const failedRole = rolesBeingUpdated[action.role.key];
    delete rolesBeingUpdated[action.role.key];

    return {
      ...state,
      rolesBeingUpdated,
      roles: roleAdapter.updateOne(
        {
          id: failedRole.key,
          changes: failedRole,
        },
        state.roles,
      )
    };
  }),
  on(RoleActions.deleteRole, (state, action) => {
    return {
      ...state,
      rolesBeingDeleted: {
        ...state.rolesBeingUpdated,
        [action.role.key]: state.roles.entities[action.role.key]
      },
      roles: roleAdapter.removeOne(
        action.role.key,
        state.roles,
      )
    };
  }),
  on(RoleActions.roleDeleted, (state, action) => {
    const rolesBeingDeleted = {...state.rolesBeingDeleted};
    delete rolesBeingDeleted[action.role.key];

    return {
      ...state,
      rolesBeingDeleted
    };
  }),
  on(RoleActions.roleDeleteFailed, (state, action) => {
    const rolesBeingDeleted = {...state.rolesBeingDeleted};
    const failedRole = rolesBeingDeleted[action.role.key];
    delete rolesBeingDeleted[action.role.key];

    return {
      ...state,
      rolesBeingDeleted,
      roles: roleAdapter.addOne(
        failedRole,
        state.roles,
      )
    };
  }),
);

export function roleReducer(state: RolesState = initialState, action: Action): RolesState {
  return adapterReducer(state, action);
}
